import { Button, Option, Select, Spinner, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { PiPathFill } from 'react-icons/pi'

import GoBack from '../components/GoBack'
import { useDarkMode } from '../contexts/DarkModeContext'
import { usePageTitle } from '../hooks/toolkit'
import { useExperienceSubmissions } from '../hooks/useExperienceSubmissions'
import { useUserExperienceDetails } from '../hooks/useUserExperienceDetails'
import { SUBMISSION_OUTCOMES, SubmissionOutcome } from '../utils/helpers/submissions.helpers'
import SubmissionRow from './SubmissionRow'
import SubmissionsCounters from './SubmissionsCounters'

const ALL = 'all'

/**
 * The proposal history of an experience (12.2): a page, not a third section.
 * `Agami` and `Bozze` are lists of content; this is an analysis panel, it must
 * hold hundreds of rows, and it must be linkable.
 *
 * The guard is the endpoint's 403. Unlike SharedExperience's guard, this one IS
 * a boundary: the satellite has an owner-only policy and no client can read it
 * except through the function, which answers only to `view_submissions`.
 *
 * Read-only on purpose: nothing here writes the satellite. Its only writers are
 * the trigger of 05.2 and the rejection RPC, and the rejection is deliberately
 * NOT offered on this page (see SubmissionRow).
 */
export const ExperienceSubmissions: React.FC = () => {
  const { t } = useTranslation()
  usePageTitle(t('submissions.pageTitle'))
  const { experienceId } = useParams()
  const { darkMode } = useDarkMode()

  const { experienceDetails } = useUserExperienceDetails({ experienceId })
  const {
    outcome,
    setOutcome,
    counters,
    submissions,
    isLoading,
    isLoadingMore,
    forbidden,
    error,
    hasMore,
    loadMore,
  } = useExperienceSubmissions({ experienceId })

  const selectProps = {
    size: 'md' as const,
    menuProps: { className: 'dark:bg-primary' },
    ...(darkMode && { color: 'blue-gray' as const }),
    labelProps: {
      className: 'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
    },
  }

  return (
    <div className="py-4">
      <div className="flex items-center gap-4 mb-4">
        <GoBack />
        <Typography variant="lead" data-testid="submissions-title">
          {t('submissions.title')}
          {experienceDetails?.name && (
            <span className="text-base text-gray-600 dark:text-base-gray-500">
              {' · '}
              <PiPathFill className="inline h-4 w-4 mr-1" />
              {experienceDetails.name}
            </span>
          )}
        </Typography>
      </div>

      {isLoading && <Spinner className="h-6 w-6" />}

      {!isLoading && forbidden && (
        <Typography data-testid="submissions-forbidden">{t('submissions.forbidden')}</Typography>
      )}

      {!isLoading && error && (
        <Typography data-testid="submissions-error">{t('submissions.error')}</Typography>
      )}

      {!isLoading && !forbidden && !error && (
        <>
          <SubmissionsCounters counters={counters} />

          {counters.total === 0 ? (
            <Typography data-testid="submissions-empty">{t('submissions.empty')}</Typography>
          ) : (
            <>
              <div className="w-full md:w-56 mb-2">
                <Select
                  {...selectProps}
                  label={t('submissions.filter.label')}
                  value={outcome ?? ALL}
                  onChange={(value) =>
                    setOutcome(!value || value === ALL ? null : (value as SubmissionOutcome))
                  }
                  data-testid="submissions-outcome-filter"
                >
                  {[
                    <Option key={ALL} value={ALL} data-testid="submissions-outcome-filter-option-all">
                      <span className="text-primary dark:text-white">{t('submissions.filter.all')}</span>
                    </Option>,
                    ...SUBMISSION_OUTCOMES.map((value) => (
                      <Option
                        key={value}
                        value={value}
                        data-testid={`submissions-outcome-filter-option-${value}`}
                      >
                        <span className="text-primary dark:text-white">
                          {t(`submissions.outcome.${value}`)} ({counters[value]})
                        </span>
                      </Option>
                    )),
                  ]}
                </Select>
              </div>

              <div data-testid="submissions-list">
                {submissions.map((submission) => (
                  <SubmissionRow key={submission.id} submission={submission} />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-4">
                  <Button
                    size="sm"
                    variant="outlined"
                    color={darkMode ? 'white' : ('primary' as colors)}
                    onClick={loadMore}
                    disabled={isLoadingMore}
                    data-testid="submissions-load-more"
                  >
                    {t('submissions.loadMore')}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
